<template>
  <div class="product-line-chart">
    <!-- 加载状态 -->
    <div v-if="loading" class="chart-loading">
      <i class="el-icon-loading"></i>
      <span>数据加载中...</span>
    </div>

    <!-- 空数据状态 -->
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-s-marketing"></i>
      <span>暂无数据</span>
    </div>

    <!-- 图表容器 -->
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'product-line-chart',
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.productLines && this.data.productLines.length > 0
    },

    chartData() {
      if (!this.hasData) return { names: [], times: [], fees: [] }

      const names = []
      const times = []
      const fees = []

      this.data.productLines.forEach(item => {
        names.push(item.name || '未知产品线')
        times.push(item.times || 0)
        fees.push(item.fee || 0)
      })

      return { names, times, fees }
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    renderChart() {
      // 先检查数据，无数据时销毁图表实例
      if (!this.hasData) {
        if (this.chart) {
          this.chart.dispose()
          this.chart = null
        }
        return
      }

      if (!this.chart) return

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999'
            }
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e8eaec',
          borderWidth: 1,
          textStyle: {
            color: '#1f2d3d'
          }
        },
        legend: {
          data: ['调用次数', '费用金额'],
          top: '0%',
          textStyle: {
            color: '#1f2d3d',
            fontSize: 12
          },
          itemWidth: 20,
          itemHeight: 12
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '12%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.names,
          axisPointer: {
            type: 'shadow'
          },
          axisLabel: {
            color: '#606266',
            fontSize: 11,
            rotate: 30,
            formatter: (value) => {
              return value.length > 6 ? value.substring(0, 6) + '...' : value
            }
          },
          axisLine: {
            lineStyle: {
              color: '#e8eaec'
            }
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '调用次数',
            nameTextStyle: {
              color: '#606266',
              fontSize: 12
            },
            axisLabel: {
              color: '#606266',
              fontSize: 11
            },
            splitLine: {
              lineStyle: {
                color: '#e8eaec',
                type: 'dashed'
              }
            }
          },
          {
            type: 'value',
            name: '费用金额(元)',
            nameTextStyle: {
              color: '#606266',
              fontSize: 12
            },
            axisLabel: {
              color: '#606266',
              fontSize: 11,
              formatter: '¥{value}'
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '调用次数',
            type: 'bar',
            data: this.chartData.times,
            barWidth: '50%',
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#667eea' },
                { offset: 1, color: '#764ba2' }
              ]),
              borderRadius: [4, 4, 0, 0]
            },
            label: {
              show: true,
              position: 'top',
              color: '#1f2d3d',
              fontSize: 10
            }
          },
          {
            name: '费用金额',
            type: 'line',
            yAxisIndex: 1,
            data: this.chartData.fees,
            smooth: true,
            lineStyle: {
              width: 3,
              color: '#fa7c91'
            },
            itemStyle: {
              color: '#fa7c91',
              borderWidth: 2,
              borderColor: '#fff'
            },
            symbol: 'circle',
            symbolSize: 8,
            label: {
              show: true,
              position: 'top',
              color: '#fa7c91',
              fontSize: 10,
              formatter: '¥{c}'
            }
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    }
  }
}
</script>

<style scoped>
.product-line-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 380px;
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 380px;
  color: #909399;
  font-size: 14px;
}

.chart-loading i,
.chart-empty i {
  font-size: 48px;
  margin-bottom: 12px;
}
</style>




// WEBPACK FOOTER //
// src/custom/components/stats/face-stats/components/product-line-chart.vue